import {
    Box,
    Button,
    Typography,
    Grid,
    TextField,
    MenuItem,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useState } from 'react';
import {
    useAsignarPrestadorMutation,
    useGetPrestadoresQuery
} from '../../../services/periodicosApi';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';

interface PeriodicosExamAndPrestadorDrawerProps {
    setOpen: (state: string) => void;
    employeeName: string;
    employeeRisk: string;
    selectedEmployee: any;
    reloadGrid: () => void;
}

const PeriodicosExamAndPrestadorDrawer = ({
    setOpen,
    employeeName,
    employeeRisk,
    selectedEmployee,
    reloadGrid
}: PeriodicosExamAndPrestadorDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const dispatch = useAppDispatch();

    const [selectedPrestadorId, setSelectedPrestadorId] = useState<number | null>(
        selectedEmployee?.prestador?.id || null
    );
    const [asignarPrestador, { isLoading }] = useAsignarPrestadorMutation();
    const { data: prestadores = [] } = useGetPrestadoresQuery({});
    const currentPrestador = prestadores.find((p) => p.id === selectedPrestadorId);
    const handleCloseDrawer = () => setOpen('');

    const handleSave = async () => {
        const empleadoId = Number(String(selectedEmployee?.id).split('-')[0]);

        if (!selectedPrestadorId || !empleadoId) {
            enqueueSnackbar(t('enqueue.assignPrestador.missingData'), { variant: 'error' });
            return;
        }

        try {
            await asignarPrestador({
                idExamenEmpleados: [empleadoId],
                idPrestador: selectedPrestadorId
            }).unwrap();

            enqueueSnackbar(t('enqueue.assignPrestador.success'), { variant: 'success' });
            setOpen('');
            reloadGrid();
        } catch (err: any) {
            enqueueSnackbar(err?.data?.message || t('enqueue.assignPrestador.error'), {
                variant: 'error'
            });
        }
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={`${selectedEmployee?.empleado?.nombre || ''} - ${
                        selectedEmployee?.riesgo?.codigo || ''
                    }  ${selectedEmployee?.riesgo?.descripcion || ''}`}
                />

                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                    {t('drawer.exam.subtitle')}
                </Typography>

                <Box
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: '#E0EBED'
                    }}
                >
                    <Typography variant="body1" fontWeight="bold">
                        {selectedEmployee?.examen?.descripcion || '-'}
                    </Typography>
                    <Typography variant="body2" mt={1}>
                        <Box component="span" fontWeight="bold">
                            {t('drawer.exam.frecuency')}
                        </Box>{' '}
                        {selectedEmployee?.examen?.frecuencia || '-'}
                    </Typography>

                    <Typography variant="body2" mt={1}>
                        <Box component="span" fontWeight="bold">
                            {t('drawer.exam.quantity')}
                        </Box>{' '}
                        {selectedEmployee?.examen?.cantidad || '-'}
                    </Typography>
                </Box>

                <Typography sx={{ fontWeight: 'bold', mb: 2, mt: 4 }}>
                    {t('drawer.prestador.subtitle')}
                </Typography>

                <TextField
                    select
                    label={t('drawer.prestador.subtitle')}
                    value={selectedPrestadorId ?? ''}
                    onChange={(e) => setSelectedPrestadorId(Number(e.target.value))}
                    fullWidth
                    sx={{ mb: 2 }}
                >
                    {prestadores.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                            {p.razonSocial}
                        </MenuItem>
                    ))}
                </TextField>

                <Box
                    sx={{
                        p: 2,
                        borderRadius: '8px',
                        backgroundColor: '#E0EBED'
                    }}
                >
                    <Typography variant="body1" fontWeight="bold">
                        {currentPrestador?.razonSocial || '-'}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight="bold">
                            {t('drawer.prestador.address')}
                        </Box>{' '}
                        {currentPrestador?.direccion || '-'}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight="bold">
                            {t('drawer.prestador.cuit')}
                        </Box>{' '}
                        {currentPrestador?.cuit || '-'}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight="bold">
                            {t('drawer.prestador.phone')}
                        </Box>{' '}
                        {currentPrestador?.telefono || '-'}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight="bold">
                            {t('drawer.prestador.mail')}
                        </Box>{' '}
                        {currentPrestador?.email || '-'}
                    </Typography>
                </Box>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer}>
                    {t('drawer.close')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{ ml: 2, textTransform: 'none', minWidth: 100 }}
                    disabled={!selectedPrestadorId || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default PeriodicosExamAndPrestadorDrawer;
